// Central, pure rules for the restricted "staff" role (own-calendar-only logins).
// Kept dependency-free so it can be unit-tested and reused by the sidebar, the
// route guard, and the login flow. These are UI gates only — the real barrier is
// the database RLS; never rely on this alone.

export type TenantRole =
  | "owner"
  | "admin"
  | "manager"
  | "member"
  | "viewer"
  | "staff";

export const isStaffRole = (role?: string | null): boolean => role === "staff";

/** Where a staff user lands after login (and is redirected to from blocked routes). */
export const STAFF_LANDING_PATH = "/calendar";

/**
 * Top-level areas a staff user may reach. The calendar is always allowed; the
 * customer list only when the salon granted staff full customer access
 * (binary: full list or none).
 */
export function staffAllowedPaths(opts?: { customerList?: boolean }): string[] {
  const paths = ["/calendar"];
  if (opts?.customerList) paths.push("/customers");
  return paths;
}

/** Is this route allowed for a staff user? Matches the path or any subpath. */
export function isPathAllowedForStaff(
  pathname: string,
  opts?: { customerList?: boolean },
): boolean {
  return staffAllowedPaths(opts).some(
    (p) => pathname === p || pathname.startsWith(p + "/"),
  );
}

/** Keep only the nav items a staff user may see (by path). */
export function filterNavItemsForStaff<T extends { path: string }>(
  items: T[],
  isStaff: boolean,
  opts?: { customerList?: boolean },
): T[] {
  if (!isStaff) return items;
  return items.filter((item) => isPathAllowedForStaff(item.path, opts));
}
